import React, { useState } from 'react';
import { toast } from 'sonner';
import { Panel, Empty } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { api, errMsg, fmtUsd, fmtDate } from '../../lib/api';

const inp = 'h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground focus:border-vj-neon/60 focus:outline-none';
const EMPTY = { user_id: '', trading_amount: '', pnl: '', note: '' };

export function TradingReports() {
  const { data: users } = useFetch('/admin/users');
  const { data: rows, reload } = useFetch('/admin/trading/reports');
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.user_id) return toast.error('Select a user');
    if (!(Number(f.trading_amount) >= 0) || f.trading_amount === '') return toast.error('Enter the trading amount');
    if (f.pnl === '' || Number.isNaN(Number(f.pnl))) return toast.error('Enter the P&L (use - for a loss)');
    setBusy(true);
    try {
      await api.post('/admin/trading/reports', { ...f, note: f.note || null });
      toast.success('Trading result posted — user can see it now');
      setF(EMPTY); reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <Panel title="Post Trading Result to a User" testId="trading-report-form-panel">
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <select value={f.user_id} onChange={set('user_id')} className={inp} data-testid="trading-report-user">
            <option value="">Select user…</option>
            {(users || []).filter((u) => u.role === 'user').map((u) => <option key={u.id} value={u.id}>{u.email} · alloc {fmtUsd(u.allocation)}</option>)}
          </select>
          <input type="number" step="0.01" min="0" value={f.trading_amount} onChange={set('trading_amount')} placeholder="Trading amount (USDT)" className={`${inp} font-mono`} data-testid="trading-report-amount" />
          <input type="number" step="0.01" value={f.pnl} onChange={set('pnl')} placeholder="P&L (e.g. 12.5 or -4)" className={`${inp} font-mono`} data-testid="trading-report-pnl" />
          <input value={f.note} onChange={set('note')} placeholder="Note (optional)" maxLength={200} className={inp} data-testid="trading-report-note" />
          <button disabled={busy} className="h-11 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm disabled:opacity-60" data-testid="trading-report-submit">Post result</button>
        </form>
        <p className="text-[11px] text-vj-grey mt-3">Adds the P&L to the user's realized P&L and pending settlement, writes a ledger entry, and notifies the user. Results show on their Dashboard and S.I. Bot page.</p>
      </Panel>
      <Panel title="Posted Results" testId="trading-report-list">
        {rows && rows.length ? (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-vj-grey border-b border-border"><th className="py-2 font-medium">User</th><th className="font-medium">Trading amount</th><th className="font-medium">P&L</th><th className="font-medium">Note</th><th className="font-medium">By</th><th className="font-medium">Date</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="py-2.5 text-xs">{r.user_email}</td>
                <td className="font-mono">{fmtUsd(r.trading_amount)}</td>
                <td className={`font-mono ${Number(r.pnl) >= 0 ? 'text-vj-neon' : 'text-vj-red'}`}>{fmtUsd(r.pnl)}</td>
                <td className="text-xs text-vj-grey">{r.note || '—'}</td>
                <td className="text-xs text-vj-grey">{r.by}</td>
                <td className="text-xs text-vj-grey">{fmtDate(r.created_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        ) : <Empty>No results posted yet.</Empty>}
      </Panel>
    </>
  );
}
